"use client";

import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";

/**
 * Account types with NO telephony entitlement — no phone number, so no calling, texting or
 * inbound. Mirrors TELEPHONY_DISABLED_ACCOUNT_TYPES in the backend's user.model.ts; the
 * server is the authority (it refuses the voice token and blocks number provisioning), this
 * only keeps the UI honest so nothing dials a wall. A free dialer account borrows the phone
 * for the unlimited dialer alone while subscribed (TwilioDeviceContext).
 */
const TELEPHONY_DISABLED = new Set(["lite", "dialer"]);

export const accountTypeHasTelephony = (accountType?: string | null, trialAccountTypeUsed?: string | null): boolean => {
  const type = String(accountType ?? "").toLowerCase();
  if (TELEPHONY_DISABLED.has(type)) return false;
  // A 14-day trial OF Lite is Lite: no number, no calling, no texting.
  if (type === "trial" && TELEPHONY_DISABLED.has(String(trialAccountTypeUsed ?? "").toLowerCase())) return false;
  return true;
};

/** Does the signed-in user's plan include calling and texting? */
export function useTelephonyAccess(): { hasTelephony: boolean; accountType: string } {
  const { user } = useUserContext();
  const accountType = String((user?.user as any)?.accountType ?? "").toLowerCase();
  const trialType = String((user?.user as any)?.trialAccountTypeUsed ?? "").toLowerCase();

  // Default to ALLOWED when the account type has not loaded yet. Hiding the dialer on an
  // empty value would blank it out for a moment on every page load for paying users — far
  // more damaging than briefly showing it to a Lite user, whose calls the server refuses
  // anyway.
  return { hasTelephony: accountTypeHasTelephony(accountType || undefined, trialType || undefined), accountType };
}

interface TelephonyEnabledProps {
  children: React.ReactNode;
  /** Render instead of the children when the plan has no telephony (e.g. an upgrade card). */
  fallback?: React.ReactNode;
}

/**
 * Renders children only for plans that include calling and texting.
 *
 * Same shape as ViewerDisabled so there is one way this app withholds a feature, not two.
 */
export default function TelephonyEnabled({ children, fallback }: TelephonyEnabledProps) {
  const { hasTelephony } = useTelephonyAccess();
  if (!hasTelephony) return <>{fallback ?? null}</>;
  return <>{children}</>;
}
